import { formatDate } from '@angular/common';

import { environment } from '@Environment/environment';
import { LOCALE_CODES } from '@VineyardLibConstant/constant';

/**
 * yyyyMMdd→yyyy-MM-dd変換
 * @param value サーバからの日付(yyyyMMdd)
 * @returns Date型の引数に該当するフォーマット(yyyy-MM-dd)
 */
export function formatToDateParsableString(value: string | undefined): string {
  if (value == null) {
    return '';
  }
  return value.slice(0, 4) + '-' + value.slice(4, 6) + '-' + value.slice(6);
}

/**
 * date→yyyyMMdd文字列の変換、フォーマットは固定のため、jaロカール固定で良い
 * @param value クライアントからの日付
 * 　　　　　　　 Date: datepicker等の値
 *               string:「August 19, 1975 23:15:30 GMT+00:00」のようなDate型に変換できるstring ※yyyyMMddで渡すとInvalidDateとなるため注意
 * @returns yyyyMMddの日付 ※InvalidDateのときは引数を返却する
 */
export function formatToDateString(
  value?: Date | string | null
): string | undefined {
  if (value == null) {
    return undefined;
  }
  // valueをDateに変換できないの場合、そのまま返す
  if (typeof value === 'string' && isNaN(new Date(value).getTime())) {
    return value;
  }
  return formatDate(value, 'yyyyMMdd', 'ja');
}

/**
 * date→yyyy-MM-dd文字列の変換、フォーマットは固定のため、localeのja固定で良い
 * formatToDateParsableString(formatToDateString())の処理と同じ
 * @param value クライアントからの日付
 *              Date: datepicker等の値
 *              string:「August 19, 1975 23:15:30 GMT+00:00」のようなDate型に変換できるstring ※yyyyMMddで渡すとInvalidDateとなるため注意
 * @returns yyyy-MM-ddの日付 ※InvalidDateのときは 1970-01-01 を返却する
 */
export function formatDateToStringWithHyphen(value: Date | string): string {
  return formatDate(value, 'yyyy-MM-dd', 'ja');
}

/**
 * hhmm->hh:mm文字列の変換
 * @param timeString hhmm形式の文字列
 * @returns hh:mm形式の文字列
 */
export function formatTimeToStringWithColon(
  timeString: string | undefined
): string {
  if (timeString?.length !== 4) {
    return timeString ?? '';
  }
  const hour = timeString.slice(0, 2);
  const min = timeString.slice(2, 4);
  return `${hour}:${min}`;
}

/**
 * セマンティックバージョニングに合わせる
 * 例：「1.1.1.1」 → 「1.1.1」
 * @param version アプリバージョン
 * @returns "."で区切った3つの整数の文字列
 */
export function formatToSemanticVersion(version: string): string {
  // 本番環境ではない場合、変換しない
  if (!environment.production) {
    return version;
  }

  const splittedStr = version.split('.');
  if (splittedStr.length < 4) {
    return version;
  }
  return splittedStr.slice(0, 3).join('.');
}

/**
 * Date型をString型(HH:mm)に変換
 * ※'shortTime' を使うと00~09時が0~9時になり、<input type="time"> で表示できなくなる
 * 日本語、英語、フランス語の時刻表示は同じため、HH:mm で表示する
 * @param value Date型の日付
 * @returns 「HH:mm」形式のString型の日付
 */
export function formatDateToHHmmString(value: Date | undefined): string {
  return value == null ? '' : formatDate(value, 'HH:mm', LOCALE_CODES.JA);
}
